import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getDeviceError,
  getDeviceErrorsByIds,
  getDeviceErrorsCount,
  getDeviceErrorsGantt,
  getDevicesAvailabilityByDeviceTag,
  getPaginatedDeviceErrors,
} from "@/api/resources/deviceErrors/api";
import {
  type GetDeviceErrorsCount,
  type GetDeviceErrorsGanttProps,
  type GetDevicesAvailabilityByDeviceTag,
  type GetPaginatedDeviceErrorsProps,
} from "@/api/resources/deviceErrors/types";
import { DEVICE_ERRORS_REFETCH_INTERVAL } from "@/configs/api";
import i18n from "@/providers/i18n";

import type { QueryPaginationFilters } from "@/utils/pagination";

const entity = i18n.t("error");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("errors");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const deviceErrorsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "deviceErrors"],
  gantt: (props: GetDeviceErrorsGanttProps) => [...deviceErrorsQueryKeys.all(), "gantt", props],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...deviceErrorsQueryKeys.all(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  count: (props: GetDeviceErrorsCount) => [deviceErrorsQueryKeys.all(), "count", props],
  availabilityByDeviceTag: (props: GetDevicesAvailabilityByDeviceTag) => [
    deviceErrorsQueryKeys.all(),
    "availabilityByDeviceTag",
    props,
  ],
  byId: (id?: number) => [...deviceErrorsQueryKeys.all(), "byId", id],
  listByIds: (ids?: number[]) => [...deviceErrorsQueryKeys.all(), "listByIds", ids],
};

export const deviceErrorsQueries = {
  gantt: (props: GetDeviceErrorsGanttProps) =>
    queryOptions({
      queryKey: deviceErrorsQueryKeys.gantt(props),
      queryFn: () => getDeviceErrorsGantt(props),
      meta: { errorMessage: entitiesErrorMessage },
    }),
  listPaginated: (queryKeyPaginationFilters: GetPaginatedDeviceErrorsProps, withPolling = false) =>
    queryOptions({
      queryKey: [...deviceErrorsQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedDeviceErrors(queryKeyPaginationFilters),
      refetchInterval(query) {
        return withPolling && query.isActive() ? DEVICE_ERRORS_REFETCH_INTERVAL : undefined;
      },
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  count: (props: GetDeviceErrorsCount) =>
    queryOptions({
      queryKey: [...deviceErrorsQueryKeys.count(props)],
      queryFn: props.devicesIn.length > 0 ? () => getDeviceErrorsCount(props) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  availabilityByDeviceTag: (props: GetDevicesAvailabilityByDeviceTag) =>
    queryOptions({
      queryKey: [...deviceErrorsQueryKeys.availabilityByDeviceTag(props)],
      queryFn: () => getDevicesAvailabilityByDeviceTag(props),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: number) =>
    queryOptions({
      queryKey: [...deviceErrorsQueryKeys.byId(id)],
      queryFn: id ? () => getDeviceError({ match: { id: id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  listByIds: (ids?: number[]) =>
    queryOptions({
      queryKey: [...deviceErrorsQueryKeys.listByIds(ids)],
      queryFn: () => getDeviceErrorsByIds(ids),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
};
